/* The order record itself — `/sales/orders/:id`'s content, and later a drawer over the list
   (ORD-3) reusing the same component. Takes finished data (`item`, already-loaded) and a `lang`;
   knows nothing about `:id`, the request, or the loading/error states — that is `Page.js`'s job.

   Read-only: the scope excludes create/edit/delete/status changes entirely (1C is the source of
   truth), so unlike every other archetype-B page in this app there is no dirty state and no save
   bar — closer in shape to `/answers/:id` (no sticky command bar) than to a form.

   Layout, as an order page reads elsewhere (Shopify, Stripe): the head (number, status, order type,
   external id, date) and the tiles (amount due, lines, discount, delivery date); then the items with
   their summary, the comment, and short titled groups — "Деталі", "Контрагент", "Доставка"
   (`OrderGroups`). On the page the groups are `Page.js`'s rail, with the map; in the modals one
   column under the items. Each value is on the screen once, and none of them is lost.

   Field shapes come from the ACTUAL `excalibur/orders/{id}/` response: `status` has no
   `status_display`, `order_type` and `counterparty` are nested objects, `point` and `supervisor`
   come through the visit (null without one); `department` / `created` / `modified` are absent.

   Money: the API counts all of it (Decimal, rounded as the mobile app does) and this file only
   formats it — except the per-unit price difference on a line's badge and the head's units count,
   which the API does not send. An item's `amount` is its UNIT price paid (promo or regular),
   `regular_price` the one before a promo (null on older orders), `regular_line_total` /
   `line_total` its line sum before / after the promo; a line 1C has recalculated carries
   `recalculated_price` / `recalculated_line_total` (else null). The items table has one "Ціна" column, as the mobile app shows it: the final unit price
   (recalculated, else the one paid) semibold, and over it — only when it differs from the regular
   one — the regular price struck through beside a tinted badge of the per-unit difference; "Сума" is
   the final line sum. The summary is the items side — `regular_total`, −`discount_total` (the promo
   discount alone), −`items_discount` (older orders only), the signed `recalculated_total_delta` —
   and ends in `payable_total`, the figure the head's "До сплати" tile and the list show too.
   Delivery is not in it: `shipping_cost` sits in the "Доставка" group. */

import React from "react";
import { Flex, Table, Tag, Tooltip, Typography } from "antd";
import { HourglassOutlined, LinkOutlined, ShoppingCartOutlined, SyncOutlined, WarningOutlined } from "@ant-design/icons";

import { DetailGroup, KpiStrip } from "../../component/detail-page";
import { PersonCell, StatusIcon, dash, textOf } from "../../component/table-cells";
import { ORDERS_PAGE } from "../../config/lang";
import dayjs from "../../util/dayjs";
import { formatMoney } from "../../util/formatter";
import { translate } from "../../util/util";

import { parseGeo } from "./GeoSection";
import { STATUS_ICON } from "./parts";

const isZero = v => v === null || v === undefined || Number(v) === 0;

/** A signed amount — the typographic minus (U+2212), "+" for a rise. */
const signed = v => `${Number(v) < 0 ? "\u2212" : "+"}${formatMoney(Math.abs(Number(v)))}`;
/** A percentage, a space before the sign — the discount tile and the order's discount row alike. */
const percent = (v, digits) => `${Number(v).toFixed(digits)}\u00a0%`;

/** items/units, once — the header's KPI strip is the only current reader, but kept as a named
    helper (mirrors `returns/ReturnDetail.js`'s own `itemTotals`) rather than inlined, so a second
    reader doesn't quietly recompute it a different way. */
const itemTotals = item => ({
  itemsCount: item.items?.length || 0,
  unitsCount: (item.items || []).reduce((sum, row) => sum + (Number(row.quantity) || 0), 0),
});

/* The 1C recalculation request (`recalculation_status`), as a pill in the head's badge row: tone,
   glyph and label, the words around its time (`recalculation_status_modified_at`), and the tooltip that says
   it in full. `null` (never requested) and a value not listed here draw no pill. The pill is the
   status pill's own markup (`status-tag`), so the row reads as one set; no system is named in it. */
const RECALC = {
  pending: {
    tone: "wait",
    Icon: HourglassOutlined,
    label: "recalc_pending",
    when: "recalc_pending_since",
    tip: "recalc_pending_tip",
    tipBare: "recalc_pending_tip_bare",
  },
  recalculated: { tone: "ok", Icon: SyncOutlined, label: "recalc_done", when: null, tip: "recalc_done_tip", tipBare: "recalc_done_tip_bare" },
};

function RecalcTag({ item, t }) {
  const meta = RECALC[item.recalculation_status];
  if (!meta) return null;
  const at = item.recalculation_status_modified_at ? dayjs(item.recalculation_status_modified_at).format("DD.MM.YYYY HH:mm") : null;
  const when = at && (meta.when ? t(meta.when).replace("{date}", at) : at);
  return (
    <Tooltip title={at ? t(meta.tip).replace("{date}", at) : t(meta.tipBare)}>
      <Tag variant="filled" icon={<meta.Icon />} className={`status-tag status-tag--${meta.tone} dp-recalc`}>
        <span className="dp-tag__part">{t(meta.label)}</span>
        {when ? (
          <>
            {" "}
            <span className="dp-tag__part dp-tag__when">· {when}</span>
          </>
        ) : null}
      </Tag>
    </Tooltip>
  );
}

/** The page head — entity icon, number, status, order type, the 1C reference, and the KPI strip.
    Its own export because `Page.js` needs it OUTSIDE `dp-grid` (spec/03 archetype B), and because
    the list's record modal reuses it verbatim as the antd `Modal` `title` — one header for the
    record, not a page version and a separate, thinner modal version that drift apart. Icon is
    `ShoppingCartOutlined`, the same glyph `AppNav.js` uses for the "Замовлення" nav row. */
export function OrderDetailHead({ item, lang }) {
  const t = key => translate(ORDERS_PAGE, key, lang);
  const { Icon, tone } = STATUS_ICON[item.status] || STATUS_ICON.new;
  const statusLabel = translate(ORDERS_PAGE, `order_status_${item.status}`, lang) || item.status;
  const { itemsCount, unitsCount } = itemTotals(item);

  return (
    <div className="dp-head dp-head--fluid">
      <div className="dp-head__body">
        <div className="dp-head__top">
          <div className={`dp-head__icon dp-head__icon--${tone}`}>
            <ShoppingCartOutlined />
          </div>
          <div className="dp-head__main">
            <div className="dp-head__titlerow">
              <h1 className="dp-title">{item.name ? textOf(item.name) : `#${item.id}`}</h1>
              {/* The filled pill `visit/parts.js`'s `StatusTag` already established — a `-bg`
                 tint plus a `-700` label plus an icon, never colour alone (spec/01#status-colour). */}
              <Tag variant="filled" icon={<Icon />} className={`status-tag status-tag--${tone}`}>
                {statusLabel}
              </Tag>
              {/* Order type is our own classification, not a verdict — a plain bordered pill,
                 same weight as the external reference beside it, neither one a status. */}
              {item.order_type?.name ? <Tag>{textOf(item.order_type.name)}</Tag> : null}
              {/* Deliberately names no system: `external_id` is this record's id in whatever
                 system feeds ours, and which one that is belongs to the deployment, not to the
                 field. The label lives in the lang group, so it is four locales rather than a
                 Cyrillic "1С:" hard-coded into the English, Armenian and Georgian UI too. */}
              {item.external_id ? (
                <Tag icon={<SyncOutlined />}>
                  {t("k_external_id")}: {textOf(item.external_id)}
                </Tag>
              ) : null}
              {/* Last in the row: where the order's prices stand with the recalculation. */}
              <RecalcTag item={item} t={t} />
            </div>
            {/* The date only: the counterparty is the "Контрагент" group's, and order type is in the
                badge row above — each value once on the screen. */}
            {item.datetime ? <Typography.Text type="secondary">{dayjs(item.datetime).format("DD.MM.YYYY HH:mm")}</Typography.Text> : null}
          </div>
        </div>
        {/* Always the same FOUR tiles — see `returns/ReturnDetail.js` for why a tile is never
            dropped when its value is missing. */}
        <KpiStrip
          items={[
            /* "До сплати" is the summary's "Всього до сплати" and the list's "Сума" — one figure. */
            { key: "total", label: translate(ORDERS_PAGE, "k_due", lang), value: formatMoney(item.payable_total) || dash },
            { key: "positions", label: translate(ORDERS_PAGE, "k_positions_units", lang), value: `${itemsCount} / ${unitsCount}` },
            {
              key: "discount",
              label: translate(ORDERS_PAGE, "k_discount_total", lang),
              /* The money and its share of the order together — the promo discount alone (the API's
                 rule: neither the items discount nor a recalculation is in it). */
              value: isZero(item.discount_total)
                ? dash
                : [formatMoney(item.discount_total), item.discount_percent != null ? percent(item.discount_percent, 1) : null].filter(Boolean).join(" · "),
            },
            {
              key: "delivery",
              label: translate(ORDERS_PAGE, "k_delivery_date", lang),
              value: item.delivery_date ? dayjs(item.delivery_date).format("DD.MM.YYYY") : dash,
            },
          ]}
        />
      </div>
    </div>
  );
}

/* The items table's numeric columns are fixed, right-aligned, and never wrap a number
   (`dp-nowrap`); a header may wrap, but between words — uk / en single-word headers are sized not to
   break mid-word (ka's long words may). Sized with the cells' own fonts, 16px of padding included:
   "Кількість" to its uk header (65px; "2400" is 33px) → 88; "Ціна" to its top line on one row — the
   12px struck regular price, a gap and the delta badge ("10815.06 −1234.56", dev's largest price)
   → 136; "Сума" to the final total in the summary under it (16px / 600, "2777816.64" ≈ 87px) → 104
   — orders of 2.8 million exist. "Назва" takes the rest and wraps, never clipped; `NAME_MIN` is its
   floor once the table scrolls. ⚠ `scroll.x` must stay above the fixed sum, or the name column is
   left 0px wide. */
const QTY_W = 88;
const PRICE_W = 136;
const SUM_W = 104;
const NAME_MIN = 160;

/** A line's unit prices: `base` the regular one (`amount` on older orders, which lack it), `final`
    the one the line ends at (1C's recalculated, else the one paid — promo or not), and `delta` the
    per-unit difference rounded to cents. The API sends Decimals as strings, so they are compared
    as numbers; a delta that rounds to 0 is no change. */
const linePrice = row => {
  const base = row.regular_price ?? row.amount;
  const final = row.recalculated_price ?? row.amount;
  const delta = base == null || final == null ? 0 : Math.round((Number(final) - Number(base)) * 100) / 100;
  return { base, final, delta };
};

/** The geolocation fact for a place with no map (the modals): the point count, escalating to the
    icon + red text when any point is fake — `isFake` is a fraud signal, so it has to be seen without
    opening the page. Shown when empty too ("no data"): no proof of where the order was taken is
    itself worth knowing. */
const GeoFact = ({ item, t }) => {
  const points = parseGeo(item.tracked_geolocation);
  const fake = points.filter(p => p.isFake).length;
  if (!points.length) return <Typography.Text type="secondary">{t("geo_no_data")}</Typography.Text>;
  if (fake)
    return (
      <Flex align="center" gap={6}>
        <StatusIcon Icon={WarningOutlined} tone="no" label={t("geo_fake_label")} />
        <Typography.Text type="danger">
          {t("geo_fake_count_label")}: {fake} / {points.length}
        </Typography.Text>
      </Flex>
    );
  return (
    <Typography.Text>
      {t("geo_points_count_label")}: {points.length}
    </Typography.Text>
  );
};

/**
 * Everything about the order that is not its items, in this order: "Деталі" (with the point),
 * "Контрагент", "Доставка". Each value is on the screen once — the head carries the number, status,
 * order type, external id and date, the tiles the money and the delivery date — and a field is
 * shown only when it holds something, the visit ("—": taken outside a visit) and the geolocation
 * ("no data") excepted. A group left with nothing is not drawn.
 *
 * `place`: "page" — the rail, where the map (`GeoSection`) follows these and the counterparty is
 * `Page.js`'s own card (`CounterpartySection`), passed in as `counterparty` so it takes its place
 * between the two groups; "modal" — one column under the items, with its own "Контрагент" (the
 * name row is "Назва", as the title says whose it is) and the geolocation fact.
 * `hideCounterparty`: the counterparty card's own modal, where that group is left out.
 */
export function OrderGroups({ item, lang, place = "modal", hideCounterparty = false, counterparty = null }) {
  const t = key => translate(ORDERS_PAGE, key, lang);
  const modal = place === "modal";
  const priceType = item.price_type ? translate(ORDERS_PAGE, `price_type_${item.price_type}`, lang) || item.price_type : null;
  /* The order's own discount % is told in the summary's "Знижка на позиції" row; it lands here only
     when that row is not drawn (no items discount), so it never disappears from the screen. */
  const orphanDiscount =
    Number(item.discount) && isZero(item.items_discount) ? { key: "discount", label: t("k_order_discount"), value: percent(item.discount, 0) } : null;
  return (
    <>
      <DetailGroup
        title={t("sec_details")}
        rows={[
          item.created_by ? { key: "created_by", label: t("k_created_by"), value: <PersonCell user={item.created_by} strong={false} /> } : null,
          {
            key: "visit",
            label: t("k_visit"),
            value: item.visit ? (
              <Typography.Link href={`/visit/${item.visit}`} target="_blank" rel="noopener noreferrer">
                {t("k_visit")} <LinkOutlined />
              </Typography.Link>
            ) : (
              dash
            ),
          },
          item.point?.name ? { key: "point", label: t("k_point"), value: textOf(item.point.name) } : null,
          item.supervisor ? { key: "supervisor", label: t("k_supervisor"), value: <PersonCell user={item.supervisor} strong={false} /> } : null,
          priceType ? { key: "price_type", label: t("k_price_type"), value: textOf(priceType) } : null,
          orphanDiscount,
          modal ? { key: "geo", label: t("k_tracked_geolocation"), value: <GeoFact item={item} t={t} /> } : null,
          item.status_changed
            ? {
                key: "status_changed",
                label: t("k_status_changed"),
                value: <span className="dp-nowrap">{dayjs(item.status_changed).format("DD.MM.YYYY HH:mm")}</span>,
              }
            : null,
        ]}
      />
      {modal ? (
        !hideCounterparty && item.counterparty ? (
          <DetailGroup
            title={t("k_counterparty")}
            rows={[
              { key: "name", label: t("k_name"), value: textOf(item.counterparty.name) },
              item.counterparty.address ? { key: "address", label: t("k_address"), value: textOf(item.counterparty.address) } : null,
            ]}
          />
        ) : null
      ) : (
        counterparty
      )}
      <DetailGroup
        title={t("sec_delivery")}
        rows={[
          isZero(item.shipping_cost)
            ? null
            : { key: "shipping", label: t("k_shipping_cost"), value: <span className="lp-num">{formatMoney(item.shipping_cost)}</span> },
        ]}
      />
    </>
  );
}

/* `layout`: "modal" (default) puts `OrderGroups` under the items; "page" leaves them to `Page.js`'s
   rail. `hideCounterparty` is for the counterparty card's Orders tab, whose modal is open over that
   very counterparty. */
const OrderDetail = ({ item, lang, showHeader = true, hideCounterparty = false, layout = "modal" }) => {
  const t = key => translate(ORDERS_PAGE, key, lang);

  /* The unit price as the mobile app shows it: the final price alone when nothing changed it;
     otherwise the regular price struck through over it, beside the per-unit difference — green
     (`status-tag--ok`) when the line got cheaper, red (`status-tag--no`) when dearer — and the
     final price semibold under both. */
  const priceCell = row => {
    const { base, final, delta } = linePrice(row);
    if (final == null) return dash;
    if (!delta) return <span className="lp-num dp-nowrap">{formatMoney(final)}</span>;
    return (
      <div className="dp-price">
        <div className="dp-price__was">
          <Tooltip title={t("k_price_regular")}>
            <s className="lp-num">{formatMoney(base)}</s>
          </Tooltip>
          <Tag variant="filled" className={`status-tag status-tag--${delta < 0 ? "ok" : "no"} dp-price__delta lp-num`}>
            {signed(delta)}
          </Tag>
        </div>
        <Typography.Text strong className="lp-num">
          {formatMoney(final)}
        </Typography.Text>
      </div>
    );
  };

  const itemColumns = [
    /* The line's position in the order as the API sent it — derived from the render index, not
       stored: nothing on `OrderItem` records a line number. `pagination={false}`, so it is 1..N. */
    { key: "line", title: t("k_line_no"), width: 48, align: "right", render: (value, row, index) => <span className="lp-num">{index + 1}</span> },
    /* The name wraps rather than clipping — the one flexible column. */
    { key: "name", title: t("k_product_name"), render: (value, row) => textOf(row.product?.name) },
    {
      key: "quantity",
      title: t("k_quantity"),
      width: QTY_W,
      align: "right",
      render: (value, row) => <span className="lp-num dp-nowrap">{textOf(row.quantity)}</span>,
    },
    { key: "price", title: t("k_price"), width: PRICE_W, align: "right", render: (value, row) => priceCell(row) },
    /* The final line sum — after a recalculation if there was one. */
    {
      key: "sum",
      title: t("k_amount"),
      width: SUM_W,
      align: "right",
      render: (value, row) => (
        <Typography.Text strong className="lp-num dp-nowrap">
          {formatMoney(row.recalculated_line_total ?? row.line_total) || dash}
        </Typography.Text>
      ),
    },
  ];
  /* The summary's label spans every column but "Сума"; its value sits under "Сума". */
  const labelSpan = itemColumns.length - 1;
  const scrollX = itemColumns.reduce((sum, col) => sum + (col.width || NAME_MIN), 0);

  /* One summary row: the label over every column but the last, the value under "Сума". */
  const summaryRow = (key, label, value, { first = false, final = false } = {}) => (
    <Table.Summary.Row key={key} style={{ background: "var(--surface-subtle)" }}>
      <Table.Summary.Cell index={0} colSpan={labelSpan} className={first ? "dp-summary-cell" : undefined}>
        {final ? <Typography.Text strong>{label}</Typography.Text> : <span className="dp-summary-label">{label}</span>}
      </Table.Summary.Cell>
      <Table.Summary.Cell index={labelSpan} align="right" className={`dp-nowrap${first ? " dp-summary-cell" : ""}`}>
        {final ? (
          /* The one deliberately larger number in this card — 16px is `dp-sec-title`'s own step,
             matching `ReturnDetail`'s own emphasized summary figure. */
          <Typography.Text strong className="lp-num" style={{ fontSize: 16 }}>
            {value}
          </Typography.Text>
        ) : (
          <span className="lp-num">{value}</span>
        )}
      </Table.Summary.Cell>
    </Table.Summary.Row>
  );
  /* `recalculated_total_delta` is null exactly when the order has no recalculation. */
  const isRecalculated = item.recalculated_total_delta !== null && item.recalculated_total_delta !== undefined;

  return (
    <>
      {showHeader ? <OrderDetailHead item={item} lang={lang} /> : null}

      {/* Totals live as a footer of THIS table, not a separate "Підсумки" card — the same choice
         `returns/ReturnDetail.js` makes and for the same reason: the total READS as part of the
         table (it sits directly under "Сума", the column it sums) instead of a heading that
         merely happens to be nearby. Only the last row is emphasized, since "Всього" is the number
         someone opens this card to find. `dp-summary-cell` (`detail-page.scss`) draws the divider
         as a class — `Table.Summary.Cell` does not forward a `style` prop at all, see that file. */}
      <div className="dp-card">
        <Typography.Text strong className="dp-sec-title">
          {t("sec_items")}
        </Typography.Text>
        {isRecalculated ? <span className="dp-sec-note">{t("k_recalc_note")}</span> : null}
        {/* `tableLayout="fixed"`: each numeric column is exactly its width and "Назва" takes the
            rest — with `auto` the browser hands spare room to the widest cells, not the name. */}
        <Table
          tableLayout="fixed"
          rowKey="id"
          size="middle"
          columns={itemColumns}
          dataSource={item.items || []}
          pagination={false}
          scroll={{ x: scrollX }}
          locale={{ emptyText: t("no_items") }}
          summary={() => (
            <Table.Summary fixed="bottom">
              {/* The items side of the order, ending in the amount due. A zero discount or
                  recalculation difference is no row at all; delivery is in the "Доставка" group. */}
              {summaryRow("regular_total", t("k_regular_total"), formatMoney(item.regular_total), { first: true })}
              {isZero(item.discount_total) ? null : summaryRow("promo", t("k_promo_discount"), signed(-Number(item.discount_total)))}
              {/* The order's discount % rides on the row it produced (old orders only). */}
              {isZero(item.items_discount)
                ? null
                : summaryRow(
                    "items_discount",
                    Number(item.discount) ? `${t("k_items_discount")} · ${percent(item.discount, 0)}` : t("k_items_discount"),
                    signed(-Number(item.items_discount))
                  )}
              {isZero(item.recalculated_total_delta) ? null : summaryRow("recalc", t("k_recalc_diff"), signed(item.recalculated_total_delta))}
              {summaryRow("due", t("k_total_due"), formatMoney(item.payable_total), { final: true })}
            </Table.Summary>
          )}
        />
      </div>
      {/* A comment carries the reason behind the order, which reads as content, not metadata —
         its own card under the items (`additional_info` is dropped, see `returns/ReturnDetail.js`). */}
      {item.comments ? (
        <div className="dp-card">
          <Typography.Text strong className="dp-sec-title">
            {t("k_comment")}
          </Typography.Text>
          <div className="dp-note__box">{textOf(item.comments)}</div>
        </div>
      ) : null}
      {layout === "modal" ? <OrderGroups item={item} lang={lang} place="modal" hideCounterparty={hideCounterparty} /> : null}
    </>
  );
};

export default OrderDetail;
